/**
 * 🎓 VoucherPanel — 算力券管理（教育版专用）
 *
 * 学校不收费，老师用这里批量生成券码、打印发给学生，学生登录后兑换成积分。
 *
 * ⚠️ 合规红线：这个界面上**不允许出现任何金额/价格/付费字样**。
 *    券的单位是「积分」（算力），不是钱，也不关联任何订单。
 *    改这个文件时如果手滑加了「元/￥/价格/售价」，deploy-frontend.sh 的
 *    产物体检会在上传前拦下来。
 *
 * 商业版根本不会渲染它（入口在 DashboardPage 里被 IS_EDU 折叠掉），
 * 后端 /api/admin/vouchers/* 在商业版也不挂载。
 */

import React, { useState, useEffect } from 'react';
import { X, Ticket, Copy, Check, Trash2, Loader2, RefreshCw, Download } from 'lucide-react';
import api from '../api';
import AlertToast from './AlertToast';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

export default function VoucherPanel({ open, onClose }) {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0, creditsRedeemed: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  // 生成参数
  const [count, setCount] = useState('20');
  const [credits, setCredits] = useState('100');
  const [batch, setBatch] = useState('');
  const [validDays, setValidDays] = useState('');
  // 筛选
  const [filterBatch, setFilterBatch] = useState('');
  const [filterUsed, setFilterUsed] = useState('');

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: '', type: 'success' }), 2500);
      return () => clearTimeout(timer);
    }
  }, [toast.message]);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const qs = [];
      if (filterBatch) qs.push(`batch=${encodeURIComponent(filterBatch)}`);
      if (filterUsed !== '') qs.push(`used=${filterUsed}`);
      const data = await api.get(`/api/admin/vouchers${qs.length ? '?' + qs.join('&') : ''}`);
      setCodes(data.codes || []);
      setStats(data.stats || { total: 0, used: 0, unused: 0, creditsRedeemed: 0 });
    } catch (err) {
      console.error('加载算力券失败:', err);
      setToast({ message: '加载算力券失败', type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterBatch, filterUsed]);

  const handleGenerate = async () => {
    const n = parseInt(count, 10);
    const c = parseInt(credits, 10);
    if (!(n > 0) || !(c > 0)) {
      setToast({ message: '数量和积分都要填大于 0 的整数', type: 'warning' });
      return;
    }
    setGenerating(true);
    try {
      const body = { count: n, credits: c };
      if (batch.trim()) body.batch = batch.trim();
      if (validDays && parseInt(validDays, 10) > 0) body.valid_days = parseInt(validDays, 10);
      const result = await api.post('/api/admin/vouchers/generate', body);
      if (result.success) {
        setToast({ message: `成功生成 ${n} 张算力券`, type: 'success' });
        // 生成后清掉筛选，否则新券可能被筛在外面、看起来像没生成
        setFilterBatch('');
        setFilterUsed('');
        loadCodes();
      }
    } catch (err) {
      console.error('生成算力券失败:', err);
      setToast({ message: err.message || '生成失败', type: 'warning' });
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (code, id) => {
    if (await copyTextToClipboard(code)) {
      setCopiedId(id);
      setToast({ message: '已复制到剪贴板', type: 'success' });
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      setToast({ message: '复制失败', type: 'warning' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/vouchers/${id}`);
      setCodes(prev => prev.filter(c => c.id !== id));
      setToast({ message: '算力券已删除', type: 'success' });
    } catch (err) {
      setToast({ message: err.message || '删除失败', type: 'warning' });
    }
  };

  // 导出当前列表为 CSV，老师拿去做分发表格 / 打印
  // 在前端拼是故意的：数据本来就已经在手上了，没必要为此加一个后端接口。
  const handleExport = () => {
    if (!codes.length) return;
    const header = '券码,积分,批次,状态,兑换人,兑换时间\n';
    const body = codes.map(c => [
      c.code, c.credits, c.batch || '',
      c.used ? '已兑换' : '未兑换',
      c.used_by_username || '',
      c.used_at || ''
    ].join(',')).join('\n');
    // ﻿ = BOM，不加的话 Excel 打开中文全是乱码
    const blob = new Blob(['﻿' + header + body], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `算力券_${filterBatch || '全部'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const isExpired = (c) => !c.used && c.expires_at && new Date(c.expires_at) < new Date();

  if (!open) return null;

  const inputStyle = {
    padding: '10px 8px', borderRadius: 8, textAlign: 'center',
    background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none'
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: 640, maxHeight: '84vh', background: 'var(--bg-panel)',
        borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border-subtle)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Ticket size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
                算力券管理
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                共 {stats.total} 张 · 未兑换 {stats.unused} · 已兑换 {stats.used} · 已发放 {stats.creditsRedeemed} 积分
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', padding: 8,
            cursor: 'pointer', color: 'var(--text-muted)'
          }}>
            <X size={20} />
          </button>
        </div>

        {/* 生成区 */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--modal-input-border)',
          display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap'
        }}>
          <input type="number" min="1" max="500" value={count} onChange={e => setCount(e.target.value)}
            style={{ ...inputStyle, width: 64 }} title="本次生成几张（最多 500）" />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>张 ×</span>
          <input type="number" min="1" value={credits} onChange={e => setCredits(e.target.value)}
            style={{ ...inputStyle, width: 76 }} title="每张券可兑换的积分" />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>积分</span>
          <input type="text" placeholder="批次(如 高二3班)" value={batch} onChange={e => setBatch(e.target.value)}
            style={{ ...inputStyle, width: 130, textAlign: 'left', paddingLeft: 10 }}
            title="批次标签，方便按班级筛选和导出" />
          <input type="number" min="1" placeholder="有效天数" value={validDays} onChange={e => setValidDays(e.target.value)}
            style={{ ...inputStyle, width: 84 }} title="留空 = 永不过期" />
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              flex: 1, minWidth: 130, padding: '11px 16px', borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none', color: '#fff', fontSize: 14, fontWeight: 500,
              cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.6 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}
          >
            {generating ? (
              <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> 生成中...</>
            ) : (
              <><Ticket size={16} /> 生成</>
            )}
          </button>
        </div>

        {/* 筛选 + 导出 */}
        <div style={{
          padding: '10px 24px', borderBottom: '1px solid var(--modal-input-border)',
          display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0
        }}>
          <input type="text" placeholder="按批次筛选" value={filterBatch}
            onChange={e => setFilterBatch(e.target.value)}
            style={{ ...inputStyle, width: 150, textAlign: 'left', paddingLeft: 10, padding: '8px 10px' }} />
          <select value={filterUsed} onChange={e => setFilterUsed(e.target.value)}
            style={{ ...inputStyle, padding: '8px 10px', cursor: 'pointer' }}>
            <option value="">全部状态</option>
            <option value="0">未兑换</option>
            <option value="1">已兑换</option>
          </select>
          <div style={{ flex: 1 }} />
          <button onClick={handleExport} disabled={!codes.length} title="导出为 CSV"
            style={{
              padding: '8px 12px', borderRadius: 8, background: 'var(--modal-input-bg)',
              border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
              cursor: codes.length ? 'pointer' : 'not-allowed', opacity: codes.length ? 1 : 0.4,
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13
            }}>
            <Download size={15} /> 导出
          </button>
          <button onClick={loadCodes} disabled={loading} title="刷新列表"
            style={{
              padding: '8px 12px', borderRadius: 8, background: 'var(--modal-input-bg)',
              border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
              cursor: 'pointer', display: 'flex', alignItems: 'center'
            }}>
            <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>

        {/* 列表 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : codes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <Ticket size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>暂无算力券</p>
              <p style={{ fontSize: 13 }}>在上方设定数量和积分后点「生成」</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {codes.map(c => {
                const expired = isExpired(c);
                const dim = c.used || expired;
                return (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', borderRadius: 10,
                    background: dim ? 'var(--modal-input-bg)' : 'rgba(16,185,129,0.08)',
                    border: `1px solid ${dim ? 'var(--modal-input-border)' : 'rgba(16,185,129,0.2)'}`,
                    opacity: dim ? 0.6 : 1
                  }}>
                    <code style={{
                      fontFamily: 'monospace', fontSize: 14, fontWeight: 600, letterSpacing: 1,
                      color: dim ? 'var(--text-muted)' : '#34d399', flexShrink: 0
                    }}>
                      {c.code}
                    </code>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>
                      {c.credits} 积分
                    </span>
                    {c.batch && (
                      <span style={{
                        fontSize: 11, color: 'var(--text-muted)', padding: '2px 8px',
                        borderRadius: 4, background: 'var(--modal-input-bg)', flexShrink: 0
                      }}>{c.batch}</span>
                    )}
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
                      {c.used
                        ? `${c.used_by_username || '已兑换'} · ${formatDate(c.used_at)}`
                        : expired ? '已过期' : '未兑换'}
                    </span>
                    <button onClick={() => handleCopy(c.code, c.id)} title="复制券码"
                      style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
                      {copiedId === c.id ? <Check size={16} color="#34d399" /> : <Copy size={16} />}
                    </button>
                    {/* 已兑换的不给删 —— 删了积分流水就对不上账，后端也会拦 */}
                    {!c.used && (
                      <button onClick={() => handleDelete(c.id)} title="删除"
                        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AlertToast message={toast.message} type={toast.type} />
    </div>
  );
}
